import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cross2Icon } from '@radix-ui/react-icons';
import { createPortal } from 'react-dom';

interface ImageUsageAlertProps {
  count: number;
  max: number;
  onClose: () => void;
  /** milliseconds to auto hide; defaults to 15000 */
  autoHideMs?: number;
  /** whether the alert is visible */
  open: boolean;
  /** optional reset timestamp (ISO string) when quota refreshes */
  resetTimestamp?: string;
  /** render as overlay using portal (doesn't affect layout) */
  overlay?: boolean;
  /** distance from bottom when overlaying (px) */
  bottomOffset?: number;
  remaining?: number; // remaining images
  rawResetUtc?: string | undefined; // raw UTC for potential future use
  source?: 'server' | 'local'; // indicates where data came from
}

// Helper for circle progress message for images remaining
const buildMessage = (count: number, max: number) => {
  if (count >= max) return `${max}/${max} images used today`;
  if (count === 1) return `1/${max} image used today`;
  return `${count}/${max} images used today`;
};

export const ImageUsageAlert: React.FC<ImageUsageAlertProps> = ({
  count,
  max,
  remaining,
  onClose,
  autoHideMs = 15000,
  open,
  resetTimestamp,
  rawResetUtc,
  overlay = true,
  bottomOffset = 110
}) => {
  
  // Track current theme mode (light/dark) so we can style the surface per request
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof document === 'undefined') return false;
    const theme = document.documentElement.getAttribute('data-theme') || '';
    return theme.startsWith('dark-');
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if (typeof document === 'undefined') return;
      const theme = document.documentElement.getAttribute('data-theme') || '';
      const darkNow = theme.startsWith('dark-');
      setIsDark(prev => prev === darkNow ? prev : darkNow);
    }, 500); // lightweight polling (theme changes are infrequent)
    return () => clearInterval(interval);
  }, []);
  // Auto hide timer
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(onClose, autoHideMs);
    return () => clearTimeout(t);
  }, [open, autoHideMs, onClose]);

  // Circle progress math
  const radius = 24; // px
  const stroke = 6;
  const normalizedRadius = radius - stroke / 2;
  const circumference = 2 * Math.PI * normalizedRadius;
  const progressRatio = Math.min(count / max, 1);
  const strokeDashoffset = useMemo(() => circumference * (1 - progressRatio), [circumference, progressRatio]);
  const message = buildMessage(count, max);
  const isComplete = count >= max;

  if (!open) return null;

  const body = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 4 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="flex justify-center bg-transparent"
        style={overlay ? { pointerEvents: 'none' } : undefined}
      >
        <div
          className="relative flex items-center gap-4 rounded-lg px-4 py-3 border shadow-sm max-w-sm"
          style={{
            background: isDark ? '#202024' : 'var(--color-secondary)',
            borderColor: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.25)',
            color: 'var(--color-text)',
            pointerEvents: 'auto'
          }}
          role="status"
          aria-live="polite"
        >
          <div className="relative" style={{ width: radius * 2, height: radius * 2 }}>
            <svg height={radius * 2} width={radius * 2}>
              <circle
                stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.12)'}
                fill="transparent"
                strokeWidth={stroke}
                r={normalizedRadius}
                cx={radius}
                cy={radius}
              />
              <circle
                stroke={'var(--color-accent)'}
                fill="transparent"
                strokeWidth={stroke}
                strokeLinecap="round"
                strokeDasharray={`${circumference} ${circumference}`}
                style={{
                  strokeDashoffset,
                  transition: 'stroke-dashoffset 0.6s ease',
                  transform: 'rotate(-180deg)',
                  transformOrigin: '50% 50%'
                }}
                r={normalizedRadius}
                cx={radius}
                cy={radius}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold select-none">
              {Math.min(count, max)}
            </div>
          </div>
          <div className="flex-1 text-sm font-medium flex flex-col gap-1">
            <span>{isComplete ? `${message} - no images remaining.` : message}</span>
            {resetTimestamp && (() => {
              const dt = new Date(resetTimestamp);
              if (isNaN(dt.getTime())) {
                return null;
              }
              const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
              const timePart = dt.toLocaleTimeString(undefined, opts);
              const datePart = dt.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });

              return <span className="text-[11px] opacity-80">Resets at {timePart} on {datePart}</span>;
            })()}
          </div>
          <button
            aria-label="Close image usage alert"
            className="p-1 rounded hover:bg-[var(--button-background-color)] transition-colors"
            onClick={onClose}
          >
            <Cross2Icon />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );

  if (!overlay) return body;

  // Overlay mode: portal so it never shifts layout; position above chat input
  return createPortal(
    <div style={{ position: 'fixed', bottom: `${bottomOffset}px`, left: 0, right: 0, display: 'flex', justifyContent: 'center', zIndex: 90 }}>
      {body}
    </div>,
    document.body
  );
};

export default ImageUsageAlert;